import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { add, format, startOfWeek, endOfWeek, eachDayOfInterval, isSameDay, setHours, setMinutes, setSeconds } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { Event, Resource, PriceAdjustment } from './scheduling/types';
import EventModal from './scheduling/EventModal';
import AppointmentDetailsModal from './scheduling/AppointmentDetailsModal';
import EditAvailabilityModal from './scheduling/EditAvailabilityModal';
import CalendarHeader from './scheduling/CalendarHeader';
import CalendarControls from './scheduling/CalendarControls';
import CalendarView from './scheduling/CalendarView';
import { supabase } from '../../lib/supabaseClient';

// Add type declaration for the window object to include our custom property
declare global {
  interface Window {
    eventBeingEdited?: boolean;
  }
}

// Type for staff availability data passed to calendar (recurring working hours only)
interface StaffAvailabilityMap {
  recurring: { [dayOfWeek: number]: { start: string; end: string }[] };
}

const SchedulingSystem: React.FC = () => {
  const navigate = useNavigate();
  // State variables
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [view, setView] = useState<'day' | 'week'>('week');
  const [isEventModalOpen, setIsEventModalOpen] = useState<boolean>(false);
  const [isAppointmentDetailsModalOpen, setIsAppointmentDetailsModalOpen] = useState<boolean>(false);
  const [isAvailabilityModalOpen, setIsAvailabilityModalOpen] = useState<boolean>(false);
  const [currentEvent, setCurrentEvent] = useState<Event | null>(null);
  const [selectedAppointment, setSelectedAppointment] = useState<Event | null>(null);
  const [modalDefaultDate, setModalDefaultDate] = useState<Date | null>(null);
  const [selectedStaffId, setSelectedStaffId] = useState<string | null>(null);
  const [staffMembers, setStaffMembers] = useState<Array<{id: string, name: string, role: string, photo: string}>>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [resources, setResources] = useState<Resource[]>([]);
  const [fetchVersion, setFetchVersion] = useState(0);
  const [staffAvailability, setStaffAvailability] = useState<StaffAvailabilityMap>({ recurring: {} });
  
  // Load staff members from team table
  useEffect(() => {
    // Add event listener for editing events
    const handleEditEvent = (e: any) => {
      const event = e.detail;
      setCurrentEvent(event);
      setModalDefaultDate(null);
      setIsEventModalOpen(true);
    };

    // Add event listener for viewing appointment details
    const handleViewAppointmentDetails = (e: any) => {
      const event = e.detail;
      setSelectedAppointment(event);
      setIsAppointmentDetailsModalOpen(true);
    };

    window.addEventListener('edit-event', handleEditEvent);
    window.addEventListener('view-appointment-details', handleViewAppointmentDetails);

    const fetchStaff = async () => {
      const { data, error } = await supabase
        .from('team')
        .select('id, name, role, photo')
        .eq('is_active', true)
        .order('display_order');
        
      if (data && !error) {
        setStaffMembers(data);
        // Find 'Larina' and set as default selected staff
        const larina = data.find(staff => staff.name === 'Larina');
        if (larina) {
          setSelectedStaffId(larina.id);
        } else if (data.length > 0) {
          // Fallback to the first staff member if Larina is not found
          setSelectedStaffId(data[0].id);
        }
      } else if (error) {
        // console.error('Error fetching staff:', error);
      }
    };
    
    fetchStaff();

    // Cleanup event listeners when component unmounts
    return () => {
      window.removeEventListener('edit-event', handleEditEvent);
      window.removeEventListener('view-appointment-details', handleViewAppointmentDetails);
    };
  }, []);

  // Create Resource objects from staffMembers
  useEffect(() => {
    if (staffMembers.length > 0) {
      const updatedResources: Resource[] = staffMembers.map((staff, index) => ({
        id: index + 1, 
        name: staff.name,
        staffId: staff.id 
      }));
      setResources(updatedResources);
    }
  }, [staffMembers]);

  // Fetch appointments and blocked times
  useEffect(() => {
    const fetchAppointmentsAndBlockedTimes = async () => {
      if (resources.length === 0 && staffMembers.length > 0) {
        return;
      }
      if (staffMembers.length === 0) {
        return;
      }
      
      // Only fetch data when the view or date changes, not on every state update
      const weekStart = startOfWeek(currentDate, { locale: enUS });
      const weekEnd = endOfWeek(currentDate, { locale: enUS });
      let allDisplayEvents: Event[] = [];

      try {
        // Fetch both data types in parallel for better performance
        const [appointmentsResponse, availabilityResponse] = await Promise.all([
          supabase
            .from('appointments')
            .select(`
              *,
              user_profiles(full_name, preferred_name, email, phone_number),
              appointment_services(
                id,
                service_id,
                price,
                duration,
                services(id, name)
              ),
              appointment_price_adjustments(
                id,
                original_total,
                adjusted_total,
                discount_percent,
                adjustment_reason,
                adjusted_by,
                adjusted_by_role,
                created_at
              )
            `)
            .gte('start_time', weekStart.toISOString())
            .lte('end_time', weekEnd.toISOString()),
          supabase
            .from('staff_availability')
            .select('*')
            .gte('specific_date', format(weekStart, 'yyyy-MM-dd'))
            .lte('specific_date', format(weekEnd, 'yyyy-MM-dd'))
        ]);

        // Process appointments
        if (!appointmentsResponse.error && appointmentsResponse.data) {
          const activeAppointments = appointmentsResponse.data.filter(apt => apt.status !== 'cancelled');
          const processedAppointments = activeAppointments.map(apt => {
            const linkedResource = resources.find(r => r.staffId === apt.staff_id);
            if (!linkedResource) return null;
            // @ts-ignore
            const clientProfile = apt.user_profiles;
            const clientName = clientProfile ? (clientProfile.preferred_name || clientProfile.full_name || 'Unknown Client') : (apt.client_id ? `Client ID: ${apt.client_id.substring(0,8)}` : 'Guest');

            // @ts-ignore - Extract services from appointment_services join
            const appointmentServices = apt.appointment_services || [];
            const services = appointmentServices.map((as: any) => ({
              id: as.service_id,
              name: as.services?.name || 'Unknown Service',
              price: as.price || 0,
              duration: as.duration || 0
            }));

            // Build title with client name and services
            const serviceNames = services.map((s: any) => s.name).join(', ');
            const title = serviceNames ? `${clientName} - ${serviceNames}` : `${clientName} - Appointment`;

            // Calculate total amount
            const totalAmount = services.reduce((sum: number, s: any) => sum + s.price, 0);

            // Get price adjustment if exists (it's an array with single item due to UNIQUE constraint)
            // @ts-ignore
            const priceAdjustmentData = apt.appointment_price_adjustments?.[0];
            const priceAdjustment: PriceAdjustment | null = priceAdjustmentData ? {
              id: priceAdjustmentData.id,
              appointment_id: apt.id,
              original_total: priceAdjustmentData.original_total,
              adjusted_total: priceAdjustmentData.adjusted_total,
              discount_percent: priceAdjustmentData.discount_percent,
              adjustment_reason: priceAdjustmentData.adjustment_reason,
              adjusted_by: priceAdjustmentData.adjusted_by,
              adjusted_by_role: priceAdjustmentData.adjusted_by_role,
              created_at: priceAdjustmentData.created_at,
            } : null;

            return {
              id: apt.id,
              title: title,
              start: new Date(apt.start_time + 'Z').toISOString(),
              end: new Date(apt.end_time + 'Z').toISOString(),
              resourceId: linkedResource.id,
              client: clientName,
              status: apt.status as Event['status'],
              notes: apt.notes || '',
              crewNotes: apt.crew_notes || '',
              recurring: false,
              isBlockedTime: false,
              services: services,
              clientEmail: clientProfile?.email || apt.client_email || '',
              clientPhone: clientProfile?.phone_number || apt.client_phone || '',
              paymentStatus: apt.payment_status as Event['paymentStatus'],
              appointmentSource: apt.source as Event['appointmentSource'],
              totalAmount: totalAmount,
              adjustedTotal: apt.adjusted_total ?? null,
              priceAdjustment: priceAdjustment,
            };
          }).filter(event => event !== null) as Event[];
          allDisplayEvents = allDisplayEvents.concat(processedAppointments);
        }

        // Process blocked times
        if (!availabilityResponse.error && availabilityResponse.data) {
          const fetchedBlockedTimes = availabilityResponse.data.map(block => {
            const linkedResource = resources.find(r => r.staffId === block.staff_id);
            if (!linkedResource) return null;
            // Blocked times are stored as local (Texas) time strings, not UTC
            // Don't append 'Z' so the browser interprets them as local time
            return {
              id: block.id,
              title: block.notes || 'Blocked Time',
              start: `${block.specific_date}T${block.start_time}`,
              end: `${block.specific_date}T${block.end_time}`,
              resourceId: linkedResource.id,
              client: '',
              status: 'cancelled' as Event['status'],
              notes: block.notes || '',
              recurring: block.is_recurring || false,
              isBlockedTime: true,
            };
          }).filter(event => event !== null) as Event[];
          allDisplayEvents = allDisplayEvents.concat(fetchedBlockedTimes);
        }
      } catch (err) {
        console.error('Error fetching calendar data:', err);
      }
      
      setEvents(allDisplayEvents);
    };

    fetchAppointmentsAndBlockedTimes();
  }, [currentDate, resources, view, fetchVersion]);

  // Setup realtime subscription for appointment changes (admin sees all)
  useEffect(() => {
    if (!selectedStaffId) return;

    const channel = supabase
      .channel('admin-appointments-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'appointments',
          filter: `staff_id=eq.${selectedStaffId}`
        },
        (payload) => {
          // Trigger refetch by incrementing fetchVersion
          setFetchVersion(prev => prev + 1);
        }
      )
      .subscribe();

    // Cleanup subscription on unmount
    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedStaffId]);

  // Fetch recurring staff availability for the selected staff member (for grey overlay)
  // Note: Blocked times (is_recurring=false) are fetched separately as events
  useEffect(() => {
    const fetchStaffAvailability = async () => {
      if (!selectedStaffId) {
        setStaffAvailability({ recurring: {} });
        return;
      }

      try {
        // Only fetch recurring availability (working hours)
        const { data: recurringData, error: recurringError } = await supabase
          .from('staff_availability')
          .select('day_of_week, start_time, end_time')
          .eq('staff_id', selectedStaffId)
          .eq('is_recurring', true);

        const availability: StaffAvailabilityMap = { recurring: {} };

        // Process recurring availability
        if (!recurringError && recurringData) {
          recurringData.forEach((record: any) => {
            if (record.day_of_week !== null) {
              if (!availability.recurring[record.day_of_week]) {
                availability.recurring[record.day_of_week] = [];
              }
              availability.recurring[record.day_of_week].push({
                start: record.start_time.substring(0, 5),
                end: record.end_time.substring(0, 5),
              });
            }
          });
        }

        setStaffAvailability(availability);
      } catch (err) {
        console.error('Error fetching staff availability:', err);
        setStaffAvailability({ recurring: {} });
      }
    };

    fetchStaffAvailability();
  }, [selectedStaffId, fetchVersion]);

  // Computed values
  const weekDays = eachDayOfInterval({
    start: startOfWeek(currentDate, { locale: enUS }),
    end: endOfWeek(currentDate, { locale: enUS })
  });
  
  // Debug weekdays information
  // console.log('Week days:', weekDays.map(d => format(d, 'yyyy-MM-dd')));
  
  // Helper functions
  const formatDateRange = (): string => {
    const start = startOfWeek(currentDate);
    const end = endOfWeek(currentDate);
    
    if (view === 'day') {
      return format(currentDate, 'MMMM d, yyyy');
    }
    
    // Week view
    if (format(start, 'MMM') === format(end, 'MMM')) {
      return `${format(start, 'MMM d')} - ${format(end, 'd, yyyy')}`;
    } else {
      return `${format(start, 'MMM d')} - ${format(end, 'MMM d, yyyy')}`;
    }
  };
  
  const navigateToPrev = (): void => {
    const newDate = view === 'day' ? add(currentDate, { days: -1 }) : add(currentDate, { weeks: -1 });
    setCurrentDate(newDate);
  };
  
  const navigateToNext = (): void => {
    const newDate = view === 'day' ? add(currentDate, { days: 1 }) : add(currentDate, { weeks: 1 });
    setCurrentDate(newDate);
  };
  
  const navigateToToday = (): void => {
    setCurrentDate(new Date());
  };
  
  const handleOpenEventModal = (dateForModal: Date | null = null): void => {
    if (!window.eventBeingEdited) {
      setCurrentEvent(null);
      setModalDefaultDate(dateForModal);
    } else {
      setModalDefaultDate(null);
    }
    window.eventBeingEdited = false;
    setIsEventModalOpen(true);
  };
  
  // Delete event handler with optimistic UI update
  const handleDeleteEvent = async (eventId: string | number): Promise<void> => {
    try {
      // Optimistically remove from UI
      const eventToDelete = events.find(e => e.id === eventId);
      if (!eventToDelete) return;
      
      // Update local state immediately
      setEvents(prev => prev.filter(e => e.id !== eventId));
      
      // Then actually delete from database
      if (eventToDelete.isBlockedTime) {
        await supabase
          .from('staff_availability')
          .delete()
          .eq('id', eventId);
      } else {
        // Update appointment status to cancelled instead of deleting
        await supabase
          .from('appointments')
          .update({ status: 'cancelled' })
          .eq('id', eventId);
      }
      
    } catch (err) {
      console.error('Error deleting event:', err);
      // If error, refresh events to get accurate state
      setFetchVersion(prev => prev + 1);
    }
  };
  
  const handleEditFromDetails = (): void => {
    // For appointments (not blocked time), redirect to booking wizard in rebook mode
    if (selectedAppointment && !selectedAppointment.isBlockedTime) {
      if (!window.confirm('This will cancel the current appointment and start a rebooking. The old appointment will be deleted. Continue?')) {
        return;
      }
      setIsAppointmentDetailsModalOpen(false);
      navigate('/booking?edit=' + selectedAppointment.id);
      return;
    }
    // For blocked time, keep existing EventModal behavior
    setIsAppointmentDetailsModalOpen(false);
    setCurrentEvent(selectedAppointment);
    setModalDefaultDate(null);
    window.eventBeingEdited = true;
    setIsEventModalOpen(true);
  };

  // Handle price adjustment - refresh data to show updated price
  const handlePriceAdjustment = (appointmentId: string, adjustedTotal: number, priceAdjustment: PriceAdjustment): void => {
    // Update the local event state with the new adjusted total and full adjustment data
    setEvents(prev => prev.map(event => {
      if (event.id === appointmentId) {
        return {
          ...event,
          adjustedTotal: adjustedTotal,
          priceAdjustment: priceAdjustment,
        };
      }
      return event;
    }));

    // Also update selectedAppointment if it's the same one
    if (selectedAppointment && selectedAppointment.id === appointmentId) {
      setSelectedAppointment(prev => prev ? {
        ...prev,
        adjustedTotal: adjustedTotal,
        priceAdjustment: priceAdjustment,
      } : null);
    }

    // Trigger a full refetch to get the complete adjustment data
    setFetchVersion(prev => prev + 1);
  };

    const hasConflicts = (event: Event | null): boolean => {
    if (!event || event.isBlockedTime) return false; // Don't check conflicts for blocked time itself
    return events.some(e => 
      e.id !== event.id && 
      !e.isBlockedTime && // Only check against other non-blocked events
      e.resourceId === event.resourceId &&
      new Date(e.start) < new Date(event.end) &&
      new Date(e.end) > new Date(event.start)
    );
  };

  // Filter events based on selected staff member
  const filteredEvents = selectedStaffId
    ? events.filter(event => {
        const resourceForEvent = resources.find(r => r.id === event.resourceId);
        if (!resourceForEvent) return false; 
        return resourceForEvent.staffId === selectedStaffId;
      })
    : events; // This fallback might be hit if selectedStaffId is null initially before Wen Li is set

  // Debug info
  // console.log('Current date:', currentDate);
  // console.log('Staff members:', staffMembers);
  // console.log('Resources:', resources);
  // console.log('Selected staff ID:', selectedStaffId);
  // console.log('Current events:', events);
  // console.log('Filtered events:', filteredEvents);

  return (
    <div className="flex flex-col h-full pt-4">
      <CalendarHeader
        onBlockTimeClick={() => handleOpenEventModal()}
        onEditAvailabilityClick={() => setIsAvailabilityModalOpen(true)}
      />
      
      {/* Calendar Controls Container */}
      <div className="bg-white rounded-lg shadow p-3 mt-2 flex-1 flex flex-col min-h-0">
        <CalendarControls
          currentDate={currentDate}
          view={view}
          formatDateRange={formatDateRange}
          onNavigatePrev={navigateToPrev}
          onNavigateToday={navigateToToday}
          onNavigateNext={navigateToNext}
          onSetView={setView}
          staffMembers={staffMembers}
          selectedStaffId={selectedStaffId}
          onStaffChange={setSelectedStaffId}
        />

        <div className="flex-1 min-h-0">
          <CalendarView
          view={view}
          weekDays={weekDays}
          currentDate={currentDate}
          events={filteredEvents}
          onOpenEventModal={handleOpenEventModal}
          onDeleteEvent={handleDeleteEvent}
          staffAvailability={staffAvailability}
          />
        </div>
      </div>

      {/* Event Modal */}
      <EventModal
        isOpen={isEventModalOpen}
        onClose={() => {
          setIsEventModalOpen(false);
          // Only trigger a re-fetch if an event was actually created or modified
          if (currentEvent) {
            setFetchVersion(prevVersion => prevVersion + 1);
          }
          setCurrentEvent(null);
          setModalDefaultDate(null);
        }}
        currentEvent={currentEvent}
        resources={resources}
        modalDefaultDate={modalDefaultDate}
        allEvents={events}
        onEventCreated={(newEvent) => {
          // Add the new event to the local state to avoid a full re-fetch
          setEvents(prev => [...prev, newEvent]);
        }}
        onEventUpdated={(updatedEvent) => {
          // Update the existing event in the local state
          setEvents(prev => prev.map(event =>
            event.id === updatedEvent.id ? updatedEvent : event
          ));
        }}
      />
      
      {/* Appointment Details Modal */}
      <AppointmentDetailsModal
        isOpen={isAppointmentDetailsModalOpen}
        onClose={() => {
          setIsAppointmentDetailsModalOpen(false);
          setSelectedAppointment(null);
        }}
        appointment={selectedAppointment}
        onEdit={handleEditFromDetails}
        onDelete={handleDeleteEvent}
        onPriceAdjustment={handlePriceAdjustment}
        maxDiscountPercent={100} // Admin has no limit
        onPaymentComplete={() => {
          setSelectedAppointment(prev => prev ? { ...prev, paymentStatus: 'paid' } : null);
          setFetchVersion(prev => prev + 1);
        }}
      />

      {/* Edit Availability Modal */}
      <EditAvailabilityModal
        isOpen={isAvailabilityModalOpen}
        onClose={() => setIsAvailabilityModalOpen(false)}
        staffMembers={staffMembers}
        initialStaffId={selectedStaffId}
        onSave={() => setFetchVersion(v => v + 1)}
      />
    </div>
  );
};

export default SchedulingSystem;
